<template>
  <div class="grid grid-rows-[1fr_auto] overflow-y-hidden md:overflow-y-auto w-full h-full justify-stretch items-center relative">
    <div v-if="!isNative" class="warning mb-4 px-4">
      <i class="pi pi-info-circle"/>
      <span>
        לצערנו לא ניתן לעשות זאת מהדפדפן. הורידו את האפליקציה שלנו, וייבאו לקוחות בקלות :)
      </span>
    </div>

    <div v-else class="flex flex-col h-full gap-2 overflow-y-scroll">
      <span class="px-4">
        בחרו את הלקוחות שלכם מאנשי הקשר שלכם, ואנחנו כבר ניצור להם משתמשים
        באפליקציה.
      </span>
      <div v-if="isLoading" class="w-full flex justify-center px-4">
        <loader/>
      </div>
      <div v-else-if="!isLoading && contactToUpload.length > 0" class="flex flex-col gap-2 px-4">
        <IconField class="w-full">
          <input-text
            v-model="searchValue"
            class="w-full"
            placeholder="חיפוש איש קשר"
            type="text"
          />
          <InputIcon class="pi pi-search"/>
        </IconField>

        <div
          v-for="(contact) in contacts"
          :key="contact.phoneNumber"
        >
          <contact-box
            :selected="selectedContacts.includes(contact.phoneNumber)"
            :phoneNumber="formatPhoneByDelimiters(contact.phoneNumber, true)"
            :fullName="contact.fullName"
            @click="clickOnContact(contact.phoneNumber)"/>
        </div>
        <span v-if="contacts.length === 0" class="text-center py-4 text-surface-500">
          לא נמצאו אנשי קשר שתואמים את החיפוש
        </span>
        <div class="h-20 w-full flex-none"></div>
      </div>
      <span v-else>
        לא הצלחנו למצוא אנשי קשר. ייתכן שלא קיבלנו הרשאות כדי להציץ ברשימת אנשי הקשר שלכם.
      </span>
    </div>
    <div v-if="contactToUpload.length > 0" class="bg-white p-4 flex justify-center items-center shadow-[0_-8px_16px_-4px_rgba(0,0,0,0.05)]">
      <Button
        v-wave
        :loading="isImportLoading"
        label=" ייבוא לקוחות לאפליקציה "
        class="w-full"
        @click="uploadContacts"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import {computed, onMounted, ref} from 'vue';
import {useToast} from 'vue-toastification';
import {useAdminClientsStore} from '@app/stores';
import {CustomersApi} from '@app/api/admin';
import {cleanPhoneNumber, cleanPhoneNumberComplete, formatPhoneByDelimiters,} from '@torim-app/utils';
import {ICustomer} from '@torim-app/types';
import {Capacitor} from '@capacitor/core';
import {Contacts} from '@skektec/capacitor-contacts';
import ContactBox from "@app/components/admin/components/ContactBox.vue";
import { trackEvent } from '@app/composables/posthog';
import InputIcon from "primevue/inputicon";
import IconField from "primevue/iconfield";

const emit = defineEmits(['close'])

const toast = useToast();
const adminClientsStore = useAdminClientsStore();

const searchValue = ref('');
const selectedContacts = ref<Array<string>>([]);
const contactToUpload = ref<Array<Partial<ICustomer>>>([]); // [{fullName: 'עומרי אברגל', phoneNumber: cleanPhoneNumberComplete('+972 52-808-7500')}]
const isLoading = ref<boolean>(false);
const isImportLoading = ref<boolean>(false);

const isNative = computed(() => Capacitor.isNativePlatform());

// Device contact names often carry bidi control marks and inconsistent spacing,
// which break a naive `includes` against what the user typed.
const normalizeName = (value: string) =>
  value
    .replace(/[\u200E\u200F\u202A-\u202E\u2066-\u2069]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const clickOnContact = (
  contactId: string,
) => {
  if (selectedContacts.value.includes(contactId)) {
    selectedContacts.value = selectedContacts.value.filter(x => x !== contactId);
  } else {
    selectedContacts.value.push(contactId)
  }
}

const chooseContacts = async () => {
  let permission = await Contacts.checkPermissions();
  if (permission.contacts !== 'granted')
    permission = await Contacts.requestPermissions();
  if (permission.contacts !== 'granted') {
    toast.error('כדי לייבא לקוחות מאנשי הקשר אנא אשרו את השימוש באנשי הקשר שלכם.')
    return;
  }

  const result = await Contacts.getContacts({
    projection: {
      name: true,
      phones: true,
    },
  });

  const seenPhoneNumbers = new Set<string>();
  contactToUpload.value = result.contacts
    .map<Partial<ICustomer>>((contact) => {
      return {
        phoneNumber: contact.phones?.[0]?.number ? cleanPhoneNumberComplete(contact.phones?.[0]?.number) : undefined,
        fullName: contact.name?.display ?? undefined,
      };
    })
    .filter((x) => !!x.phoneNumber && !!x.fullName)
    // phoneNumber is used as the v-for key and as the selection id, so duplicates
    // (same number saved under several contacts) would break rendering while filtering.
    .filter((x) => {
      if (seenPhoneNumbers.has(x.phoneNumber as string)) return false;
      seenPhoneNumbers.add(x.phoneNumber as string);
      return true;
    })
    .sort((a, b) => (a.fullName && b.fullName) ? (a.fullName > b.fullName ? 1 : -1) : 0);
};

const uploadContacts = async () => {
  trackEvent('customers_imported', { count: selectedContacts.value.length });
  try {
    isImportLoading.value = true;
    const {clientsAdded} = await CustomersApi.addCustomer(
      isNative.value ? contactToUpload.value.filter(x => x.phoneNumber && selectedContacts.value.includes(x.phoneNumber)) : contactToUpload.value
    );
    adminClientsStore.resetCustomersList();
    await adminClientsStore.fetchBusinessCustomers();
    toast.success(`איזה כיף, ייבאנו ${clientsAdded} לקוחות!`);
    emit('close');
  } catch (e: any) {
    console.error('failed to load customer-panel' + e);
    if (e === 'customer-panel already exists')
      toast.error('לא ניתן ליצור לקוח שכבר קיים במערכת');
    else toast.error('לא הצלחנו לשמור את הלקוח');
  } finally {
    isImportLoading.value = false;
  }
};

const contacts = computed(() => {
  const query = searchValue.value.trim();
  if (!query) return contactToUpload.value;

  // Search by name (case-insensitive, ignoring extra spaces) or by phone number.
  const nameQuery = normalizeName(query);
  // Stored numbers are already cleaned to the local `05...` form, so align the query with it.
  const phoneQuery = cleanPhoneNumber(query).replace(/^972/, '0');

  return contactToUpload.value.filter((x) => {
    if (nameQuery && normalizeName(x.fullName ?? '').includes(nameQuery)) return true;
    return !!phoneQuery && !!x.phoneNumber?.includes(phoneQuery);
  });
});

onMounted(() => {
  if (Capacitor.isNativePlatform()) {
    isLoading.value = true;
    setTimeout(async () => {
      await chooseContacts();
      isLoading.value = false
    }, 1200)
  }
});

</script>

<style lang="scss" scoped>
p {
  line-height: 1.5;
}

.import-customer-modal {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  padding: 0 1rem;
  gap: 1rem;

  .warning {
    display: flex;
    justify-content: center;
    align-items: center;
    background: #fff2e2;
    padding: 1rem;
    border-radius: 0.5rem;
    width: 100%;
    color: #cc8925;
    gap: 0.5rem;
  }
}
</style>
